import { useCallback, useEffect, useRef, useState } from 'react';
import { UserServices } from '../Services/UserServices';
import { formatApiErrorMessage } from '../utils/userProfileFields';
import {
    bulkUploadJobId,
    legacyBulkResult,
    normaliseBulkJob,
    pollBulkUploadJob,
} from '../utils/bulkUploadJob';

const userServices = new UserServices();

const readFileAsBase64 = file => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result ? String(reader.result).split(',')[1] || '' : '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
});

/**
 * Uploads a user bulk file. Works with the asynchronous job API (`data.job_id`, then polling)
 * and with the legacy synchronous response (`Created`/`Processed`/`Failed`).
 */
const useBulkUserUpload = ({ customerId, onFinished } = {}) => {
    const [job, setJob] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const cancelled = useRef(false);
    const onFinishedRef = useRef(onFinished);
    onFinishedRef.current = onFinished;

    useEffect(() => () => { cancelled.current = true; }, []);

    const reset = useCallback(() => {
        setJob(null);
        setError('');
    }, []);

    const upload = useCallback(async (file) => {
        if (!file) return null;
        setUploading(true);
        setError('');
        setJob(null);
        try {
            const encoded = await readFileAsBase64(file);
            if (!encoded) throw new Error('The file is empty.');
            const response = await userServices.bulkUpload({ data: encoded }, customerId);
            if (!response || response.status === false) {
                const message = formatApiErrorMessage(response, 'The file could not be uploaded.');
                setError(message);
                return null;
            }
            const jobId = bulkUploadJobId(response);
            if (!jobId) {
                const result = legacyBulkResult(response);
                setJob(result);
                if (onFinishedRef.current) onFinishedRef.current(result);
                return result;
            }
            setJob(normaliseBulkJob({ state: 'queued', ...(response.data || {}) }));
            const result = await pollBulkUploadJob(jobId, id => userServices.getBulkUploadStatus(id), {
                onProgress: next => { if (!cancelled.current) setJob(next); },
                isCancelled: () => cancelled.current,
            });
            if (!cancelled.current) {
                setJob(result);
                if (onFinishedRef.current) onFinishedRef.current(result);
            }
            return result;
        } catch (uploadError) {
            setError(uploadError?.message || 'The file could not be uploaded.');
            return null;
        } finally {
            if (!cancelled.current) setUploading(false);
        }
    }, [customerId]);

    return { job, uploading, error, upload, reset };
};

export default useBulkUserUpload;
